import {
  Questionnaire,
  QuestionnaireTemplate,
} from '@app/core/models/questionnaire.model';
import { EditingSection } from '../section-editors/editing-section';
import type {
  PreviewContext,
  WelcomePreviewData,
  BodyPreviewData,
  ExitPreviewData,
  HeaderPreviewData,
  FooterPreviewData,
  PagePreviewData,
} from './preview.types';

function mapAlignmentToFlex(alignment: 'left' | 'center' | 'right' | undefined): string {
  if (!alignment) return 'center';
  switch (alignment) {
    case 'left':
      return 'flex-start';
    case 'right':
      return 'flex-end';
    default:
      return 'center';
  }
}

function mapVerticalAlignment(alignment: string | undefined): string {
  switch (alignment) {
    case 'top':
      return 'flex-start';
    case 'bottom':
      return 'flex-end';
    default:
      return 'center';
  }
}

function mapHorizontalAlignment(alignment: string | undefined): string {
  switch (alignment) {
    case 'left':
      return 'flex-start';
    case 'right':
      return 'flex-end';
    default:
      return 'center';
  }
}

function mapTextAlign(alignment: string | undefined): string {
  switch (alignment) {
    case 'left':
      return 'left';
    case 'right':
      return 'right';
    default:
      return 'center';
  }
}

function getEditingBgColor(ctx: PreviewContext): string {
  const t = ctx.editingTemplate;
  const s = ctx.editingSection;
  if (!t || !s) return '#FFFFFF';
  if (s === EditingSection.WELCOME_SCREEN) return t.welcomeScreenTemplate?.backGround?.bgColor || '#FFFFFF';
  if (s === EditingSection.EXIT_SCREEN) return t.exitScreenTemplate?.backGround?.bgColor || '#FFFFFF';
  if (s === EditingSection.BODY_CONTENT) return t.mainContentTemplate?.backGround?.bgColor || '#FFFFFF';
  return '#FFFFFF';
}

function getCurrentTemplateBgColor(ctx: PreviewContext): string {
  const t = ctx.currentTemplate;
  const s = ctx.editingSection;
  const q = ctx.questionnaire;
  if (!t && !q) return '#FFFFFF';
  if (s === EditingSection.WELCOME_SCREEN) return t?.welcomeScreenTemplate?.backGround?.bgColor || q?.welcomeScreenTemplate?.backGround?.bgColor || '#FFFFFF';
  if (s === EditingSection.EXIT_SCREEN) return t?.exitScreenTemplate?.backGround?.bgColor || q?.exitScreenTemplate?.backGround?.bgColor || '#FFFFFF';
  if (s === EditingSection.BODY_CONTENT) return t?.mainContentTemplate?.backGround?.bgColor || q?.mainContentTemplate?.backGround?.bgColor || '#FFFFFF';
  return '#FFFFFF';
}

function resolveBgColor(ctx: PreviewContext): string {
  if (ctx.editingTemplateId && ctx.editingTemplate && ctx.editingSection) return getEditingBgColor(ctx);
  if (ctx.selectedTemplateId === ctx.questionnaire?.brandingTemplateId && ctx.questionnaire) {
    const q = ctx.questionnaire;
    const s = ctx.editingSection;
    if (s === EditingSection.WELCOME_SCREEN) return q.welcomeScreenTemplate?.backGround?.bgColor || '#FFFFFF';
    if (s === EditingSection.EXIT_SCREEN) return q.exitScreenTemplate?.backGround?.bgColor || '#FFFFFF';
    if (s === EditingSection.BODY_CONTENT) return q.mainContentTemplate?.backGround?.bgColor || '#FFFFFF';
  }
  if (ctx.previewTemplate) {
    const p = ctx.previewTemplate;
    const s = ctx.editingSection;
    if (s === EditingSection.WELCOME_SCREEN) return p.welcomeScreenTemplate?.backGround?.bgColor || ctx.questionnaire?.welcomeScreenTemplate?.backGround?.bgColor || '#FFFFFF';
    if (s === EditingSection.EXIT_SCREEN) return p.exitScreenTemplate?.backGround?.bgColor || ctx.questionnaire?.exitScreenTemplate?.backGround?.bgColor || '#FFFFFF';
    if (s === EditingSection.BODY_CONTENT) return p.mainContentTemplate?.backGround?.bgColor || ctx.questionnaire?.mainContentTemplate?.backGround?.bgColor || '#FFFFFF';
  }
  return getCurrentTemplateBgColor(ctx);
}

function getWelcomeScreenSource(ctx: PreviewContext): QuestionnaireTemplate['welcomeScreenTemplate'] | undefined {
  const q = ctx.questionnaire;
  if (ctx.editingTemplateId && ctx.editingTemplate && ctx.editingSection === EditingSection.WELCOME_SCREEN)
    return ctx.editingTemplate.welcomeScreenTemplate;
  if (ctx.selectedTemplateId === q?.brandingTemplateId && q) return q.welcomeScreenTemplate;
  if (ctx.previewTemplate) return ctx.previewTemplate.welcomeScreenTemplate;
  return ctx.currentTemplate?.welcomeScreenTemplate ?? q?.welcomeScreenTemplate;
}

export function getWelcomePreviewData(ctx: PreviewContext | null): WelcomePreviewData | null {
  if (!ctx || ctx.editingSection !== EditingSection.WELCOME_SCREEN) return null;
  const ws = getWelcomeScreenSource(ctx);
  if (!ws) return null;
  if ((ws as { enableWelcomeScreen?: boolean }).enableWelcomeScreen === false) return null;

  const bgColor = resolveBgColor(ctx);
  const logoUrl = ws.logo?.logoImage || null;
  const logoSizePx = String(ws.logo?.logoSize ?? 100);
  const title = ws.title?.text ?? '';
  const titleColor = ws.title?.fontColor ?? '';
  const titleFontSize = (ws.title?.fontSize != null ? ws.title.fontSize : '') + 'px';
  const titleFontFamily = ws.title?.fontFamily ?? '';
  const titleFontWeight = ws.title?.boldStyle ?? '';
  const description = ws.description?.text ?? '';
  const descriptionColor = ws.description?.fontColor ?? '';
  const descriptionFontSize = (ws.description?.fontSize != null ? ws.description.fontSize : '') + 'px';
  const descriptionFontFamily = ws.description?.fontFamily ?? '';
  const descriptionFontWeight = ws.description?.boldStyle ?? '';
  const buttonName = ws.button?.name ?? '';
  const buttonBgColor = ws.button?.bgColor ?? '';
  const buttonColor = ws.button?.fontColor ?? '';
  const buttonFontSize = ws.button?.fontSize != null ? String(ws.button.fontSize) : '';
  const buttonFontFamily = ws.button?.fontFamily ?? '';
  const buttonFontWeight = ws.button?.boldStyle ?? '';
  const verticalAlignment = mapVerticalAlignment(ws.displayStyle?.verticalAlignment);
  const horizontalAlignment = mapHorizontalAlignment(ws.displayStyle?.horizontalAlignment);
  const textAlign = mapTextAlign(ws.displayStyle?.horizontalAlignment);

  return {
    backgroundColor: bgColor,
    logoUrl,
    logoSizePx,
    title,
    titleColor,
    titleFontSize,
    titleFontFamily,
    titleFontWeight,
    description,
    descriptionColor,
    descriptionFontSize,
    descriptionFontFamily,
    descriptionFontWeight,
    buttonName,
    buttonBgColor,
    buttonColor,
    buttonFontSize,
    buttonFontFamily,
    buttonFontWeight,
    verticalAlignment,
    horizontalAlignment,
    textAlign,
  };
}

function getImagePositionFlag(ctx: PreviewContext): 'top' | 'bottom' {
  const es = getExitScreenSource(ctx);
  const positionFlag = es?.imageStyle?.positionFlag as string | undefined;
  if (positionFlag === 'bottom' || positionFlag === 'outside') return 'bottom';
  return 'top';
}

function getExitScreenSource(ctx: PreviewContext): QuestionnaireTemplate['exitScreenTemplate'] | undefined {
  if (ctx.editingTemplateId && ctx.editingTemplate && ctx.editingSection === EditingSection.EXIT_SCREEN)
    return ctx.editingTemplate.exitScreenTemplate;
  if (ctx.selectedTemplateId === ctx.questionnaire?.brandingTemplateId && ctx.questionnaire)
    return ctx.questionnaire.exitScreenTemplate;
  if (ctx.previewTemplate) return ctx.previewTemplate.exitScreenTemplate;
  return ctx.currentTemplate?.exitScreenTemplate ?? ctx.questionnaire?.exitScreenTemplate;
}

export function getExitPreviewData(ctx: PreviewContext | null): ExitPreviewData | null {
  if (!ctx || ctx.editingSection !== EditingSection.EXIT_SCREEN) return null;
  const es = getExitScreenSource(ctx);
  if (!es) return null;

  const bgColor = resolveBgColor(ctx);
  const title = es.thankYouNote?.text ?? '';
  const titleColor = es.thankYouNote?.fontColor ?? '';
  const titleFontSize = (es.thankYouNote?.fontSize != null ? es.thankYouNote.fontSize : '') + 'px';
  const titleFontFamily = es.thankYouNote?.fontFamily ?? '';
  const titleFontWeight = es.thankYouNote?.boldStyle ?? '';
  const description = es.subText?.text ?? '';
  const descriptionColor = es.subText?.fontColor ?? '';
  const descriptionFontSize = (es.subText?.fontSize != null ? es.subText.fontSize : '') + 'px';
  const descriptionFontFamily = es.subText?.fontFamily ?? '';
  const descriptionFontWeight = es.subText?.boldStyle ?? '';
  const imageUrl = es.imageStyle?.image || null;
  const imageSizePx = es.imageStyle?.imageSize ?? 100;
  const imagePosition = getImagePositionFlag(ctx);
  const imageHorizontalAlignment = es.imageStyle?.horizontalAlignment
    ? mapHorizontalAlignment(es.imageStyle.horizontalAlignment as 'left' | 'center' | 'right')
    : 'center';
  const verticalAlignment = mapVerticalAlignment(es.displayStyle?.verticalAlignment);
  const horizontalAlignment = mapHorizontalAlignment(es.displayStyle?.horizontalAlignment);
  const textAlign = mapTextAlign(es.displayStyle?.horizontalAlignment);

  return {
    backgroundColor: bgColor,
    title,
    titleColor,
    titleFontSize,
    titleFontFamily,
    titleFontWeight,
    description,
    descriptionColor,
    descriptionFontSize,
    descriptionFontFamily,
    descriptionFontWeight,
    imageUrl,
    imageSizePx,
    imagePosition,
    imageHorizontalAlignment,
    verticalAlignment,
    horizontalAlignment,
    textAlign,
    icxBrand: es.icxBrand,
  };
}

function getMainContentSource(ctx: PreviewContext): QuestionnaireTemplate['mainContentTemplate'] | undefined {
  if (ctx.editingTemplateId && ctx.editingTemplate && ctx.editingSection === EditingSection.BODY_CONTENT)
    return ctx.editingTemplate.mainContentTemplate;
  if (ctx.selectedTemplateId === ctx.questionnaire?.brandingTemplateId && ctx.questionnaire)
    return ctx.questionnaire.mainContentTemplate;
  if (ctx.previewTemplate) return ctx.previewTemplate.mainContentTemplate;
  return ctx.currentTemplate?.mainContentTemplate ?? ctx.questionnaire?.mainContentTemplate;
}

function getWelcomeSourceForBody(ctx: PreviewContext): QuestionnaireTemplate['welcomeScreenTemplate'] | undefined {
  if (ctx.editingTemplateId && ctx.editingTemplate && ctx.editingSection === EditingSection.BODY_CONTENT)
    return ctx.editingTemplate.welcomeScreenTemplate;
  if (ctx.selectedTemplateId === ctx.questionnaire?.brandingTemplateId && ctx.questionnaire)
    return ctx.questionnaire.welcomeScreenTemplate;
  if (ctx.previewTemplate) return ctx.previewTemplate.welcomeScreenTemplate;
  return ctx.currentTemplate?.welcomeScreenTemplate ?? ctx.questionnaire?.welcomeScreenTemplate;
}

/** Sections that use the combined preview (header + page + body + footer); body must render for all of them */
const PAGE_LAYOUT_SECTIONS: (EditingSection | string)[] = [
  EditingSection.HEADER,
  EditingSection.PAGE_TEMPLATE,
  EditingSection.BODY_CONTENT,
  EditingSection.FOOTER,
];

export function getBodyPreviewData(ctx: PreviewContext | null): BodyPreviewData | null {
  if (!ctx || !PAGE_LAYOUT_SECTIONS.includes(ctx.editingSection as EditingSection)) return null;
  const mc = getMainContentSource(ctx);
  const ws = getWelcomeSourceForBody(ctx);
  if (!mc) return null;

  const userInfoStatusCondition =
    ctx.questionnaire?.userInfoCaptureSettings?.statusCondition || 'ANONYMOUS';
  const canSkipUserInfo = userInfoStatusCondition === 'OPTIONAL';

  const bgColor =
    ctx.editingSection === EditingSection.BODY_CONTENT
      ? resolveBgColor(ctx)
      : (mc.backGround?.bgColor ?? ctx.questionnaire?.mainContentTemplate?.backGround?.bgColor ?? '#FFFFFF');
  const titleColor = mc.questionStyle?.fontColor ?? ws?.title?.fontColor ?? '';
  const titleFontSize = (mc.questionStyle?.fontSize != null ? mc.questionStyle.fontSize : ws?.title?.fontSize ?? '') + 'px';
  const titleFontFamily = mc.questionStyle?.fontFamily ?? ws?.title?.fontFamily ?? '';
  const titleFontWeight = mc.questionStyle?.boldStyle ?? ws?.title?.boldStyle ?? '';
  const descriptionColor = mc.choiceStyle?.fontColor ?? ws?.description?.fontColor ?? '';
  const descriptionFontSize = (mc.choiceStyle?.fontSize != null ? mc.choiceStyle.fontSize : ws?.description?.fontSize ?? '') + 'px';
  const descriptionFontFamily = mc.choiceStyle?.fontFamily ?? ws?.description?.fontFamily ?? '';
  const descriptionFontWeight = mc.choiceStyle?.boldStyle ?? ws?.description?.boldStyle ?? '';
  const buttonBgColor = ws?.button?.bgColor ?? '';
  const buttonColor = ws?.button?.fontColor ?? '';
  const buttonFontFamily = ws?.button?.fontFamily ?? '';
  const buttonFontWeight = ws?.button?.boldStyle ?? '';
  const horizontalAlignment = mapHorizontalAlignment(mc.layOutAlignment);
  const questionSpacing = mc.questionSpacing ?? 24;

  return {
    backgroundColor: bgColor,
    titleColor,
    titleFontSize,
    titleFontFamily,
    titleFontWeight,
    descriptionColor,
    descriptionFontSize,
    descriptionFontFamily,
    descriptionFontWeight,
    buttonBgColor,
    buttonColor,
    buttonFontFamily,
    buttonFontWeight,
    horizontalAlignment,
    currentPageQuestions: ctx.getCurrentPageQuestions?.() ?? [],
    displayPageNumber: ctx.getDisplayPageNumber?.() ?? 1,
    displayTotalPages: ctx.getDisplayTotalPages?.() ?? 1,
    canGoNext: ctx.canGoToNextPage?.() ?? false,
    canGoPrevious: ctx.canGoToPreviousPage?.() ?? false,
    isEmpty: (ctx.questionnaire?.questions?.length ?? 0) === 0,
    showUserInfoPage: ctx.shouldShowUserInfo?.() ?? false,
    currentPage: ctx.currentPage ?? 0,
    userInfoFields: ctx.questionnaire?.userInfoCaptureSettings?.userInfoFields ?? [],
    userInfoStatusCondition,
    canSkipUserInfo,
    questionSpacing,
  };
}

/** Header/footer source: prefer editingTemplate when editing that theme section (same as welcome/exit/body). */
function getEffectiveHeaderFooterSetup(ctx: PreviewContext | null) {
  if (!ctx) return null;
  const editing = ctx.editingTemplate?.headerFooterSetup;
  const isEditingHeaderFooter =
    ctx.editingSection === EditingSection.HEADER || ctx.editingSection === EditingSection.FOOTER;
  if (isEditingHeaderFooter && editing) return editing;
  return ctx.questionnaire?.headerFooterSetup ?? null;
}

/** Page template source: prefer editingTemplate when editing that theme section. */
function getEffectivePageTemplate(ctx: PreviewContext | null) {
  if (!ctx) return null;
  const editing = ctx.editingTemplate?.pageTemplate;
  if (ctx.editingSection === EditingSection.PAGE_TEMPLATE && editing) return editing;
  return ctx.questionnaire?.pageTemplate ?? null;
}

export function isHeaderEnabled(ctx: PreviewContext | null): boolean {
  const hf = getEffectiveHeaderFooterSetup(ctx) as { enableHeader?: boolean } | null;
  if (!hf) return false;
  return hf.enableHeader !== false;
}

export function isFooterEnabled(ctx: PreviewContext | null): boolean {
  const hf = getEffectiveHeaderFooterSetup(ctx) as { enableFooter?: boolean } | null;
  if (!hf) return false;
  return hf.enableFooter !== false;
}

export function getHeaderPreviewData(ctx: PreviewContext | null): HeaderPreviewData | null {
  const hf = getEffectiveHeaderFooterSetup(ctx);
  if (!hf || !ctx || !isHeaderEnabled(ctx)) return null;
  const align = hf.alignment;
  const bg = hf.background;
  const name = hf.headerName;
  const desc = hf.headerDescription;
  const img = hf.headerImage;
  return {
    enableHeader: Boolean(hf.enableHeader),
    backgroundColor: bg?.headerBgColor || '#FFFFFF',
    imageUrl: img?.imagePath || '',
    imageSizePx: img?.imageSize ?? 48,
    title: name?.text || 'Header',
    titleColor: name?.fontColor || '#000000',
    titleFontSize: String(name?.fontSize ?? 16),
    titleFontFamily: name?.fontFamily || 'Inter',
    titleFontWeight: name?.boldStyle || '400',
    description: desc?.text || '',
    descriptionColor: desc?.fontColor || '#666666',
    descriptionFontSize: String(desc?.fontSize ?? 14),
    descriptionFontFamily: desc?.fontFamily || 'Inter',
    descriptionFontWeight: desc?.boldStyle || '400',
    alignment: mapAlignmentToFlex(align?.headerAlignment as 'left' | 'center' | 'right'),
    textAlign: (align?.headerAlignment as string) || 'left',
    imageAlignment: mapAlignmentToFlex(align?.headerImageAlignment as 'left' | 'center' | 'right'),
    descriptionAlignment: mapAlignmentToFlex(align?.headerDescriptionAlignment as 'left' | 'center' | 'right'),
    descriptionTextAlign: mapTextAlign(align?.headerDescriptionAlignment as 'left' | 'center' | 'right'),
  };
}

export function getFooterPreviewData(ctx: PreviewContext | null): FooterPreviewData | null {
  const hf = getEffectiveHeaderFooterSetup(ctx);
  if (!hf || !ctx || !isFooterEnabled(ctx)) return null;
  const align = hf.alignment;
  const bg = hf.background;
  const content = hf.footerContent;
  return {
    enableFooter: Boolean(hf.enableFooter),
    backgroundColor: bg?.footerBgColor || '#F5F5F5',
    content: content?.text ?? '',
    contentColor: content?.fontColor || '#666666',
    contentFontSize: String(content?.fontSize ?? 12),
    contentFontFamily: content?.fontFamily || 'Inter',
    contentFontWeight: content?.boldStyle || '400',
    alignment: mapAlignmentToFlex(align?.footerAlignment as 'left' | 'center' | 'right'),
    textAlign: mapTextAlign(align?.footerAlignment as 'left' | 'center' | 'right'),
    linkLabel: hf.linkUrlPlaceholder || '',
    linkUrl: hf.linkUrl || '',
    promotionFlag: Boolean(hf.promotionFlag),
  };
}

/**
 * Page section preview: only for question pages (currentPage >= 1).
 * User info page (currentPage === 0) has no page section — show nothing.
 * For question pages, use pageSetup for that pageNumber for header/description text; styles from pageTemplate.
 */
export function getPagePreviewData(ctx: PreviewContext | null): PagePreviewData | null {
  const currentPage = ctx?.currentPage ?? 0;
  if (currentPage === 0) return null;

  const pt = getEffectivePageTemplate(ctx);
  if (!pt) return null;

  const pageSetup = ctx?.questionnaire?.pageSetup ?? [];
  const setup = pageSetup.find((p) => p.pageNumber === currentPage);
  /** No row yet → keep template defaults; explicit `enablePage: false` hides page header block in preview. */
  const pageTitleEnabled = !setup || setup.enablePage !== false;

  const bg = pt.background;
  const headerStyle = pt.pageHeaderStyle;
  const descStyle = pt.pageDescriptionStyle;
  const align = pt.alignment;

  return {
    backgroundColor: bg?.bgColor || '#FFFFFF',
    headerText: pageTitleEnabled ? (setup?.pageHeader ?? headerStyle?.text ?? '') : '',
    headerColor: headerStyle?.fontColor || '#000000',
    headerFontSize: String(headerStyle?.fontSize ?? 18),
    headerFontFamily: headerStyle?.fontFamily || 'Inter',
    headerFontWeight: headerStyle?.boldStyle || '600',
    descriptionText: pageTitleEnabled ? (setup?.pageDescription ?? descStyle?.text ?? '') : '',
    descriptionColor: descStyle?.fontColor || '#666666',
    descriptionFontSize: String(descStyle?.fontSize ?? 14),
    descriptionFontFamily: descStyle?.fontFamily || 'Inter',
    descriptionFontWeight: descStyle?.boldStyle || '400',
    headerAlignment: (align?.headerAlignment as string) || 'left',
    descriptionAlignment: (align?.descriptionAlignment as string) || 'left',
  };
}
